import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, Field, Input, Modal, Spinner, Toggle, useToast } from "@aigntx/ui";
import { endpoints } from "../api";
import { useAuth } from "../auth";
import type { CatalogRole, User, UserListItem } from "../types";

/** Team & access manager — embedded in the Company admin page (Team tab). */
export function TeamManager() {
  const { t } = useTranslation();
  const { user, can } = useAuth();
  const toast = useToast();
  const canManage = can("users.manage");
  const [rows, setRows] = useState<UserListItem[] | null>(null);
  const [roles, setRoles] = useState<CatalogRole[]>([]);
  const [error, setError] = useState("");
  const [form, setForm] = useState<{ mode: "create" | "edit"; target?: UserListItem } | null>(null);
  const [deleting, setDeleting] = useState<UserListItem | null>(null);

  async function confirmDelete() {
    if (!deleting) return;
    try {
      await endpoints.deleteUser(deleting.id);
      setRows((rs) => (rs ? rs.filter((r) => r.id !== deleting.id) : rs));
      toast.success(t("users.deleted"));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setDeleting(null);
    }
  }

  useEffect(() => {
    Promise.all([endpoints.listUsers(), endpoints.getCatalog()])
      .then(([u, cat]) => {
        setRows(u);
        setRoles(cat.roles);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  async function toggleActive(u: UserListItem) {
    const updated = await endpoints.updateUser(u.id, { active: !u.active });
    setRows((rs) => (rs ? rs.map((r) => (r.id === u.id ? updated : r)) : rs));
  }

  function onSaved(u: UserListItem) {
    setForm(null);
    setRows((rs) => {
      if (!rs) return rs;
      const i = rs.findIndex((r) => r.id === u.id);
      if (i >= 0) {
        const next = [...rs];
        next[i] = u;
        return next;
      }
      return [...rs, u];
    });
  }

  if (error) return <Card>{error}</Card>;
  if (!rows)
    return (
      <div className="center-pad">
        <Spinner />
      </div>
    );

  return (
    <>
      <div className="tab-toolbar">
        <p className="page__sub">{t("users.subtitle")}</p>
        {canManage && <Button onClick={() => setForm({ mode: "create" })}>{t("users.new")}</Button>}
      </div>

      <Card flush>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>{t("users.name")}</th>
                <th>{t("users.roles")}</th>
                <th>{t("users.active")}</th>
                {canManage && <th aria-label="actions" />}
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id}>
                  <td>
                    <div className="u-name">
                      {u.name}
                      {u.isPlatformAdmin && <Badge tone="accent">platform</Badge>}
                    </div>
                    <div className="u-mail mono">{u.email}</div>
                  </td>
                  <td>
                    <div className="chips">
                      {u.roles.length ? (
                        u.roles.map((r) => <Badge key={r}>{roleLabel(roles, r)}</Badge>)
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </div>
                  </td>
                  <td>
                    {canManage && u.id !== user?.id && !u.isPlatformAdmin ? (
                      <Toggle checked={u.active} onChange={() => void toggleActive(u)} id={`act-${u.id}`} />
                    ) : (
                      <Badge tone={u.active ? "good" : "danger"}>
                        {u.active ? t("users.on") : t("users.off")}
                      </Badge>
                    )}
                  </td>
                  {canManage && (
                    <td className="right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setForm({ mode: "edit", target: u })}
                        disabled={u.isPlatformAdmin && !user?.isPlatformAdmin}
                      >
                        {t("users.edit")}
                      </Button>
                      {u.id !== user?.id && !u.isPlatformAdmin && (
                        <Button size="sm" variant="ghost" onClick={() => setDeleting(u)}>
                          {t("common.delete")}
                        </Button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {form && (
        <UserFormModal
          mode={form.mode}
          target={form.target}
          roles={roles}
          me={user}
          onClose={() => setForm(null)}
          onSaved={onSaved}
        />
      )}

      {deleting && (
        <Modal
          open
          title={t("users.deleteTitle")}
          onClose={() => setDeleting(null)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setDeleting(null)}>
                {t("common.cancel")}
              </Button>
              <Button variant="danger" onClick={() => void confirmDelete()}>
                {t("common.delete")}
              </Button>
            </>
          }
        >
          <p>{t("users.deleteConfirm", { name: deleting.name })}</p>
        </Modal>
      )}
    </>
  );
}

function roleLabel(roles: CatalogRole[], key: string): string {
  return roles.find((r) => r.key === key)?.label ?? key;
}

function UserFormModal({
  mode,
  target,
  roles,
  onClose,
  onSaved,
}: {
  mode: "create" | "edit";
  target?: UserListItem;
  roles: CatalogRole[];
  me: User | null;
  onClose: () => void;
  onSaved: (u: UserListItem) => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState(target?.email ?? "");
  const [name, setName] = useState(target?.name ?? "");
  const [password, setPassword] = useState("");
  const [sel, setSel] = useState<string[]>(target?.roles ?? []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  function toggleRole(key: string) {
    setSel((s) => (s.includes(key) ? s.filter((x) => x !== key) : [...s, key]));
  }

  async function submit() {
    setBusy(true);
    setError("");
    try {
      const saved =
        mode === "create"
          ? await endpoints.createUser({ email, name, password, roles: sel })
          : await endpoints.updateUser(target!.id, { name, roles: sel });
      onSaved(saved);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={mode === "create" ? t("users.new") : t("users.edit")}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button loading={busy} onClick={() => void submit()}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      {error && <div className="form-err">{error}</div>}
      {mode === "create" && (
        <Field label={t("login.email")}>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
      )}
      <Field label={t("users.name")}>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      {mode === "create" && (
        <Field label={t("login.password")} hint={t("users.pwHint")}>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
      )}
      <Field label={t("users.roles")}>
        <div className="chips">
          {roles.map((r) => (
            <button
              type="button"
              key={r.key}
              className={sel.includes(r.key) ? "chip is-on" : "chip"}
              onClick={() => toggleRole(r.key)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </Field>
    </Modal>
  );
}
